import { FC } from 'react'
import { Label } from '@/components/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import type { ColumnJSON } from '../../../../src/types/table-json'
import { Hash } from 'lucide-react'

export interface ColumnSelectorProps {
  /**
   * The columns metadata from TableDataJSON
   */
  columns?: ColumnJSON[]
  
  /**
   * The currently selected column name
   */
  value?: string
  
  /**
   * Label to display above the selector
   */
  label?: string
  
  /**
   * Placeholder text when no column is selected
   */
  placeholder?: string
  
  /**
   * Called when a column is selected
   */
  onChange?: (column: string) => void
  
  /**
   * Whether to only show numeric columns
   */
  numericOnly?: boolean

  /**
   * Whether the selector is required
   */
  required?: boolean
  
  /**
   * Whether the selector is disabled
   */
  disabled?: boolean

  /**
   * Additional CSS classes to apply to the root element
   */
  className?: string
}

export const ColumnSelector: FC<ColumnSelectorProps> = ({
  columns = [],
  value,
  label,
  placeholder = 'Select a column...',
  onChange,
  numericOnly,
  required,
  disabled,
  className = ''
}) => {
  const numericColumns = numericOnly
    ? columns.filter(col => col.values.every((v: string) => !isNaN(Number(v))))
    : columns
  
  return (
    <div className={`space-y-2 ${className}`} data-testid="column-selector">
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium leading-none group flex items-center">
            {label}
            {numericOnly && (
              <span className="ml-1.5 text-xs text-muted-foreground inline-flex items-center">
                # numeric
              </span>
            )}
          </label>
          {required && (
            <span className="text-red-500 text-xs leading-none">*</span>
          )}
        </div>
      )}
      <Select
        value={value}
        onValueChange={onChange}
        disabled={disabled || columns.length === 0}
      >
        <SelectTrigger className="w-full h-9 transition-all hover:border-primary focus:border-primary focus:ring-1 focus:ring-primary">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-h-[280px]">
          {!required && (
            <SelectItem value="none" className="focus:bg-primary/10">
              None
            </SelectItem>
          )}
          {numericColumns.map((column) => (
            <SelectItem 
              key={column.id} 
              value={column.id} 
              className="focus:bg-primary/10 hover:bg-muted/50 transition-colors"
            >
              {column.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {value && columns.length > 0 && (
        <div className="text-xs text-muted-foreground mt-1">
          Selected: {columns.find(col => col.id === value)?.name || value}
        </div>
      )}
    </div>
  )
} 